import React, { useState } from 'react';
import { Store, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { submitDealerInquiry } from '../api/api';

const EMPTY = {
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  location: '',
  message: '',
};

export const DealerForm = ({ dark = false }) => {
  const [formData, setFormData] = useState(EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitDealerInquiry(formData);
      toast.success('Application Received!', {
        description: "Thanks for your interest in becoming a dealer. Our team will reach out shortly.",
      });
      setFormData(EMPTY);
    } catch (error) {
      toast.error('Error', { description: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelCls = dark ? 'text-gray-200 font-medium mb-2' : 'text-gray-900 font-medium mb-2';
  const inputCls = dark
    ? 'mt-2 h-12 bg-white/10 border-white/20 text-white placeholder:text-gray-400'
    : 'mt-2 h-12';

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="dealer-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="companyName" className={labelCls}>Company Name *</Label>
          <Input id="companyName" name="companyName" required value={formData.companyName}
            onChange={handleChange} placeholder="Acme Pools LLC" className={inputCls}
            data-testid="dealer-company-input" />
        </div>
        <div>
          <Label htmlFor="contactName" className={labelCls}>Contact Name *</Label>
          <Input id="contactName" name="contactName" required value={formData.contactName}
            onChange={handleChange} placeholder="Jane Smith" className={inputCls}
            data-testid="dealer-contact-input" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="dealerEmail" className={labelCls}>Email Address *</Label>
          <Input id="dealerEmail" name="email" type="email" required value={formData.email}
            onChange={handleChange} placeholder="jane@acmepools.com" className={inputCls}
            data-testid="dealer-email-input" />
        </div>
        <div>
          <Label htmlFor="dealerPhone" className={labelCls}>Phone Number *</Label>
          <Input id="dealerPhone" name="phone" type="tel" required value={formData.phone}
            onChange={handleChange} placeholder="+1 (555) 000-0000" className={inputCls}
            data-testid="dealer-phone-input" />
        </div>
      </div>

      <div>
        <Label htmlFor="location" className={labelCls}>Business Location (City, State) *</Label>
        <Input id="location" name="location" required value={formData.location}
          onChange={handleChange} placeholder="Chandler, AZ" className={inputCls}
          data-testid="dealer-location-input" />
      </div>

      <div>
        <Label htmlFor="dealerMessage" className={labelCls}>Tell Us About Your Business</Label>
        <Textarea id="dealerMessage" name="message" value={formData.message} onChange={handleChange}
          placeholder="What markets do you serve? Estimated annual volume? Anything else we should know?"
          rows={4}
          className={dark ? 'mt-2 resize-none bg-white/10 border-white/20 text-white placeholder:text-gray-400' : 'mt-2 resize-none'}
          data-testid="dealer-message-input" />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="relative z-10 w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg font-semibold rounded-xl transition-all duration-300 hover:shadow-xl"
        data-testid="dealer-submit-button"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center space-x-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Submitting...</span>
          </span>
        ) : (
          <span className="flex items-center justify-center space-x-2">
            <Store size={20} />
            <span>Become a Dealer</span>
            <Send size={18} />
          </span>
        )}
      </Button>
    </form>
  );
};

export default DealerForm;
